import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { api, type PropertyCatalogField } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import { useTenant } from "../../../contexts/TenantContext.tsx";
import CenteredSpinner from "../../../components/CenteredSpinner.tsx";

function FieldGroup({
  title,
  fields,
  selected,
  onToggle,
}: {
  title: string;
  fields: PropertyCatalogField[];
  selected: Set<string>;
  onToggle: (key: string) => void;
}) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 0.5,
        }}
      >
        {fields.map((f) => (
          <FormControlLabel
            key={f.key}
            control={
              <Checkbox
                size="small"
                checked={selected.has(f.key)}
                onChange={() => onToggle(f.key)}
              />
            }
            label={f.label}
          />
        ))}
      </Box>
    </Box>
  );
}

function RequiredPropertiesPage() {
  const { selectedTenantId } = useTenant();
  const queryClient = useQueryClient();
  const [company, setCompany] = useState<Set<string>>(new Set());
  const [contact, setContact] = useState<Set<string>>(new Set());

  const tenant = useQuery({
    queryKey: keys.tenant.detail(selectedTenantId ?? ""),
    queryFn: () => api.getTenant(selectedTenantId!),
    enabled: !!selectedTenantId,
  });

  const catalog = useQuery({
    queryKey: keys.tenant.propertyCatalog(),
    queryFn: () => api.getPropertyCatalog(),
  });

  useEffect(() => {
    if (tenant.data) {
      setCompany(new Set(tenant.data.required_company_properties));
      setContact(new Set(tenant.data.required_contact_properties));
    }
  }, [tenant.data]);

  const mutation = useMutation({
    mutationFn: () =>
      api.updateTenantRequiredProperties(selectedTenantId!, {
        required_company_properties: [...company],
        required_contact_properties: [...contact],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.tenant.detail(selectedTenantId!),
      });
      toast.success("Required properties saved");
    },
  });

  if (tenant.isLoading || catalog.isLoading) return <CenteredSpinner />;

  const toggle = (
    setter: React.Dispatch<React.SetStateAction<Set<string>>>,
  ) =>
    (key: string) =>
      setter((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });

  return (
    <>
      <FieldGroup
        title="Required company properties"
        fields={catalog.data?.company ?? []}
        selected={company}
        onToggle={toggle(setCompany)}
      />
      <FieldGroup
        title="Required contact properties"
        fields={catalog.data?.contact ?? []}
        selected={contact}
        onToggle={toggle(setContact)}
      />

      <Box>
        <Button
          variant="contained"
          onClick={() => mutation.mutate()}
          loading={mutation.isPending}
        >
          Save
        </Button>
      </Box>
    </>
  );
}

export default RequiredPropertiesPage;
